import React, { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { X } from '@phosphor-icons/react';
import { FeedbackForm } from './FeedbackForm';
import { Msg, useFormatMessage } from '../i18n-review/client/Msg';

// Add keyframe animations for smooth modal appearance
const modalStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  source?: string;
  metadata?: Record<string, unknown>;
}

export function FeedbackModal({ isOpen, onClose, source = 'general', metadata }: FeedbackModalProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <style>{modalStyles}</style>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-pointer"
        onClick={(e) => {
          // Close when clicking backdrop
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* Backdrop with blur */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-md transition-opacity duration-300 cursor-pointer"
          style={{
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            backdropFilter: 'blur(20px) saturate(180%)',
            animation: 'fadeIn 0.3s ease-out',
          }}
        />

        {/* Modal */}
        <div
          className="relative w-full max-w-lg bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] rounded-3xl shadow-2xl dark:shadow-none transform transition-all duration-300 ease-out cursor-default"
          style={{
            boxShadow: '0 20px 60px rgba(30, 26, 20, 0.2), 0 0 0 0.5px rgba(221, 213, 200, 0.5) inset',
            animation: 'modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#DDD5C8]/70 dark:border-[#4A3520]/60">
            <h2 className="text-2xl font-semibold text-[#1E1A14] dark:text-[#F2EADF] tracking-tight">
              <Msg
                id="feedback.modal.title"
                defaultMessage="Send Feedback"
                description="Title for feedback modal"
              />
            </h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-full hover:bg-[#F2EADF] dark:hover:bg-[#2A1D0F] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3B5E45]/40 dark:focus:ring-[#98CFA8]/40 cursor-pointer"
              aria-label={formatMessage({
                id: 'feedback.modal.close',
                defaultMessage: 'Close',
                description: 'Button label to close feedback modal'
              })}
            >
              <X className="w-5 h-5 text-[#7A6E5F] dark:text-[#B8A48C]" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <p className="text-sm text-[#7A6E5F] dark:text-[#B8A48C] mb-6 leading-relaxed">
              <Msg
                id="feedback.modal.description"
                defaultMessage="We'd love to hear your thoughts. Share your feedback to help us improve."
                description="Description text in feedback modal"
              />
            </p>
            <FeedbackForm
              source={source}
              metadata={metadata}
              onSuccess={(detail) => {
                const delayMs = detail?.authenticated ? 1500 : 5200;
                setTimeout(() => {
                  onClose();
                }, delayMs);
              }}
              onCancel={onClose}
            />
          </div>
        </div>
      </div>
    </>
  );
}
